import { useCallback, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'

import { Lead, useAppDispatch } from 'store'
import useQueryParam from 'hooks/use-query-param'
import useLeadDetail from 'hooks/use-lead-detail'
import { ACTIVE_LEAD_TAB_STORAGE_KEY, LEAD_TAB_ID } from 'utils/constants'
import { LeadDetailTab } from 'store/types/lead'
import useSessionStorage from 'hooks/use-session-storage'

const isValidLeadTabId = (currentTab: string | null): currentTab is LeadDetailTab =>
  Object.values(LEAD_TAB_ID).includes(currentTab as LeadDetailTab)

const useOpenExternalLeadUrl = () => {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const activeWindow = useQueryParam('window')
  const windowType = useQueryParam('window_type')
  const quotationId = useQueryParam('quotation_id')
  const meetingId = useQueryParam('meeting_id')
  const isValidTabId = isValidLeadTabId(activeWindow)

  const { leadId = '' } = useParams<{ leadId: string }>()

  const { isDispatchedToCurrentUser, data, isReadOnly, isLoading } = useLeadDetail()
  const [_currentTab, setCurrentTab] = useSessionStorage<LeadDetailTab>(
    ACTIVE_LEAD_TAB_STORAGE_KEY,
    LEAD_TAB_ID.activity,
  )

  const removeParameters = useCallback(
    (paramsArray: string[]) => {
      const searchParams = new URLSearchParams(location.search)

      paramsArray.forEach((param) => {
        searchParams.delete(param)
      })

      // Replace the current URL without the removed parameters
      navigate(
        {
          pathname: location.pathname,
          search: searchParams.toString(),
        },
        { replace: true },
      )
    },
    [navigate],
  )

  useEffect(() => {
    if (isValidTabId) {
      setCurrentTab(activeWindow)
      removeParameters(['window'])
    }

    if (!isLoading) {
      switch (windowType) {
        case 'quotation':
          dispatch(Lead.openQuotationWindow({ leadId, quotationId, isReadOnly }))
          removeParameters(['quotation_id', 'window_type'])
          break
        case 'meeting':
          dispatch(Lead.openMeetingWindow({ leadId, leadMeetingId: meetingId }))
          removeParameters(['meeting_id', 'window_type'])
          break
        default:
      }

      if (isDispatchedToCurrentUser && data?.dispatch?.leadAppointmentId) {
        dispatch(
          Lead.openMeetingWindow({
            leadId,
            leadMeetingId: data.dispatch.leadAppointmentId,
          }),
        )
      }
    }
  }, [
    dispatch,
    leadId,
    quotationId,
    windowType,
    isReadOnly,
    removeParameters,
    activeWindow,
    setCurrentTab,
    isValidTabId,
    isDispatchedToCurrentUser,
    data?.dispatch?.leadAppointmentId,
    meetingId,
    isLoading,
  ])
}

export default useOpenExternalLeadUrl
